/**
 * The rule that decides what `Why` hides, kept out of the component file.
 *
 * Its own module because `react-refresh/only-export-components` refuses a
 * component file that also exports a constant — and the rule is worth being
 * importable on its own anyway: it is a design decision with a threshold in it,
 * and the next person to question it should find it without reading JSX.
 */

/**
 * Whether a `kv` note is a justification or a qualifier.
 *
 * # Why length, and why that is not as crude as it looks
 *
 * The two kinds are not marked in the data — `KvItem` has one `note` slot and
 * eighteen blocks across thirteen adapters fill it. Adding a flag would mean
 * editing all eighteen, which turns a one-component change into a many-file
 * one and puts the classification in the hands of whoever is editing an adapter
 * next.
 *
 * They do differ reliably in shape, though. A **qualifier is terse by nature**
 * — `set by us · reason n/a`, `FinXP acceptance 2025-11-06 · not the same as
 * IDV`, `ledger · read 12:04` — because it is a stamp. A **justification is a
 * sentence**, and on the screen that prompted this the shortest was 60
 * characters and the longest 183.
 *
 * So the threshold sits above every stamp in the corpus and below every
 * paragraph. It is stated here rather than buried, and a test pins both sides
 * of it — because the failure mode of getting it wrong is a provenance stamp
 * disappearing behind a click, which is the one thing this must not do.
 *
 * If a caller ever needs to override this, the honest fix is a flag on the item
 * rather than a longer sentence to get under the bar.
 */
export const QUALIFIER_MAX = 90

export function isJustification(note: string): boolean {
  return note.length > QUALIFIER_MAX
}
